import { collection, doc, runTransaction, serverTimestamp, Timestamp } from '@firebase/firestore';

import type { ActionedBy, IntakeStatusBaseSteps, StatusActivity } from '@shared/patient-intake-status';

import { db } from '../../../../../../firebase-config.js';

type ToggleBaseStepVisibilityParams<TStep extends keyof IntakeStatusBaseSteps> = {
  stepId: TStep;
  patientId: string;
  step: IntakeStatusBaseSteps[NoInfer<TStep>] | undefined;
  isCurrentlyDisabled: boolean;
  actionedBy: ActionedBy;
};

export const toggleBaseStepVisibility = async <TStep extends keyof IntakeStatusBaseSteps>({
  step,
  stepId,
  patientId,
  actionedBy,
  isCurrentlyDisabled,
}: ToggleBaseStepVisibilityParams<TStep>) => {
  const docRef = doc(db, 'patients', patientId, 'intakeStatus', stepId);

  await runTransaction(db, async (transaction) => {
    const stepSnapshot = await transaction.get(docRef);

    const statusActivityRef = doc(collection(db, 'patients', patientId, 'intakeStatusActivity'));

    if (stepSnapshot.exists()) {
      transaction.update(docRef, {
        isDisabled: !isCurrentlyDisabled,
      });

      transaction.set(statusActivityRef, {
        stepId,
        actionedBy,
        action: 'TOGGLE_DISABLED',
        stepSnapshot: step as IntakeStatusBaseSteps[NoInfer<TStep>],
        actionedAt: serverTimestamp() as Timestamp,
        metadata: { isDisabled: !isCurrentlyDisabled },
      } satisfies StatusActivity<'TOGGLE_DISABLED'>);

      return;
    }

    if (!stepSnapshot) {
      throw new Error('Current status not found');
    }

    transaction.set(docRef, {
      ...step,
      isDisabled: !isCurrentlyDisabled,
    });

    transaction.set(statusActivityRef, {
      stepId,
      actionedBy,
      action: 'TOGGLE_DISABLED',
      stepSnapshot: step as IntakeStatusBaseSteps[NoInfer<TStep>],
      actionedAt: serverTimestamp() as Timestamp,
      metadata: { isDisabled: !isCurrentlyDisabled },
    } satisfies StatusActivity<'TOGGLE_DISABLED'>);
  });
};
